// ./src/plugins/auth.js
import Vue from 'vue'
import createAuth0Client from '@auth0/auth0-spa-js'
import auth0Config from '../config/auth0'

import router from '../router'
import store from '../store'
import axios from 'axios'
import { axiosInstance } from './axios'
import { decrypt } from '@/helpers/crypt'
import { mutations } from '../state'

let instance

const defaultCallback = () => {
  return window.history.replaceState({}, document.title, window.location.pathname)
}

export const getInstance = () => instance

export const useAuth0 = ({ onRedirect = defaultCallback, redirectUri = window.location.origin, ...options }) => {
  if (instance) {
    return instance
  }

  // Create the instance in a new Vue object.
  instance = new Vue({
    data: () => ({
      auth0Client: null
    }),
    methods: {

      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback () {
        store.dispatch('Auth/setLoading', { loading: true })
        try {
          await this.auth0Client.handleRedirectCallback()
          const user = await this.auth0Client.getUser()

          const authenticated = true
          store.dispatch('Auth/setUser', { user })
          store.dispatch('Auth/setAuthenticated', { authenticated })
        } catch (e) {
          store.dispatch('Auth/setError', { e })
        } finally {
          store.dispatch('Auth/setLoading', { loading: false })
        }
      },

      /** Authenticates the user using the redirect method */
      loginWithRedirect (o) {
        return this.auth0Client.loginWithRedirect(o)
      },

      /** Returns all the claims present in the ID token */
      getIdTokenClaims (o) {
        return this.auth0Client.getIdTokenClaims(o)
      },

      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently (o) {
        return this.auth0Client.getTokenSilently(o)
      },

      /** Gets the access token using a popup window */
      getTokenWithPopup (o) {
        return this.auth0Client.getTokenWithPopup(o)
      },

      /** Logs the user out and removes their session on the authorization server */
      logout (o) {
        o = {
          ...o,
          returnTo: auth0Config.redirect || redirectUri
        }

        localStorage.removeItem('lang')
        localStorage.removeItem('expires')
        return this.auth0Client.logout(o)
      }

    },

    /** Use this lifecycle method to instantiate the SDK client */
    async created () {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain,
        client_id: options.clientId,
        audience: options.audience,
        redirect_uri: redirectUri
      })

      store.commit('Auth/setAuth0Client', { client: this.auth0Client })

      try {
        // If the user is returning to the app after authentication..
        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
          // handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback()

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirect(appState)
        }
      } catch (e) {
        store.dispatch('Auth/setError', { e })
      } finally {
        // Initialize our internal authentication state
        const authenticated = await this.auth0Client.isAuthenticated()
        const user = await this.auth0Client.getUser()
        const loading = false

        if (user) {
          const idToken = user
          const accessToken = await this.auth0Client.getTokenSilently()
          mutations.setBearer(accessToken)
          axios.defaults.headers.common.Authorization = accessToken ? `Bearer ${accessToken}` : ''
          axiosInstance.defaults.headers.common.Authorization = accessToken ? `Bearer ${accessToken}` : ''

          store.dispatch('initialize', { idToken, accessToken, authenticated, loading })
        } else {
          store.dispatch('Auth/initialize', { loading, authenticated, user })
        }

        let lang = null
        const storedLang = localStorage.getItem('lang')
        if (storedLang) {
          const jsonString = decrypt(localStorage.getItem('lang'))
          lang = JSON.parse(jsonString)
        }

        console.log(lang)

        await store.dispatch('setLocale', { lang })
        await store.dispatch('initLocale')
      }
    }
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
const Auth0Plugin = {
  install (Vue, options) {
    Vue.auth = useAuth0(options)
    Vue.prototype.$auth = useAuth0(options)
  }
}

Vue.use(Auth0Plugin, {
  domain: auth0Config.domain,
  clientId: auth0Config.clientId,
  audience: auth0Config.audience,
  onRedirect: appState => {
    router.push(
      window.location.pathname
    )
  }
})
